import { state } from './state.js';
import * as UI from './ui.js';
import * as API from './api.js';

export const PREVIEW_BATCH_SIZE = 24;

let currentOffset = 0;
let isFetching = false;
let totalCount = 0;
let activeLibraryId = null;
let hasLoadedInitial = false;

export function resetLibraryPreviewState() {
    currentOffset = 0;
    isFetching = false;
    totalCount = 0;
    activeLibraryId = null;
    hasLoadedInitial = false;
}

function _setActiveLibrary(libraryId) {
    const nextId = String(libraryId || '');
    if (!nextId) return;
    if (activeLibraryId && activeLibraryId !== nextId) {
        resetLibraryPreviewState();
    }
    activeLibraryId = nextId;
}

export function hasLoadedInitialLibraryPreview(libraryId) {
    const safeId = String(libraryId || '');
    return Boolean(safeId && hasLoadedInitial && activeLibraryId === safeId);
}

export async function loadInitialLibraryPreview(libraryId, opts = {}) {
    const safeId = String(libraryId || '');
    if (!safeId) return null;
    _setActiveLibrary(safeId);
    const force = Boolean(opts && opts.force);
    if (hasLoadedInitial && !force) {
        return { count: totalCount, images: [] };
    }
    if (isFetching) return null;
    isFetching = true;
    const startedAt = (typeof performance !== 'undefined' && typeof performance.now === 'function')
        ? performance.now()
        : Date.now();
    try {
        const data = await API.getLibraryPreview(libraryId, PREVIEW_BATCH_SIZE, currentOffset);
        totalCount = data.count ?? totalCount;
        hasLoadedInitial = true;
        UI.renderLibraryPreview(data);
        const durationMs = (
            (typeof performance !== 'undefined' && typeof performance.now === 'function')
                ? performance.now()
                : Date.now()
        ) - startedAt;
        API.logUiFlow('library_explorer', 'load_initial', 'ok', {
            library_id: safeId,
            offset: currentOffset,
            limit: PREVIEW_BATCH_SIZE,
            returned: Array.isArray(data && data.images) ? data.images.length : 0,
            total_count: Number((data && data.count) || 0),
        }, durationMs).catch(() => {});
        return data;
    } catch (err) {
        const durationMs = (
            (typeof performance !== 'undefined' && typeof performance.now === 'function')
                ? performance.now()
                : Date.now()
        ) - startedAt;
        API.logUiFlow('library_explorer', 'load_initial', 'error', {
            library_id: safeId,
            offset: currentOffset,
            limit: PREVIEW_BATCH_SIZE,
            message: String((err && err.message) || err || 'load_failed').slice(0, 160),
        }, durationMs).catch(() => {});
        throw err;
    } finally {
        isFetching = false;
    }
}

export async function refreshSelectedLibraryPreview() {
    if (!state.selectedLibraryId) return;
    if (!hasLoadedInitialLibraryPreview(state.selectedLibraryId)) return null;
    _setActiveLibrary(state.selectedLibraryId);
    currentOffset = 0;
    totalCount = 0;
    const data = await API.getLibraryPreview(state.selectedLibraryId, PREVIEW_BATCH_SIZE, 0);
    totalCount = data.count ?? totalCount;
    hasLoadedInitial = true;
    UI.renderLibraryPreview(data);
    return data;
}

export function attachLibraryPreviewScroll() {
    const previewGrid = document.getElementById('libPreviewGrid');
    if (!previewGrid) return;

    previewGrid.onscroll = async () => {
        if (isFetching || currentOffset + PREVIEW_BATCH_SIZE >= totalCount) return;

        if (previewGrid.scrollTop + previewGrid.clientHeight >= previewGrid.scrollHeight - 50) {
            isFetching = true;
            const nextOffset = currentOffset + PREVIEW_BATCH_SIZE;
            const startedAt = (typeof performance !== 'undefined' && typeof performance.now === 'function')
                ? performance.now()
                : Date.now();

            try {
                const moreData = await API.getLibraryPreview(
                    state.selectedLibraryId,
                    PREVIEW_BATCH_SIZE,
                    nextOffset
                );
                currentOffset = nextOffset;
                totalCount = moreData.count ?? totalCount;
                if (moreData.images && moreData.images.length > 0) {
                    UI.renderLibraryPreview(moreData, true);
                }
                const durationMs = (
                    (typeof performance !== 'undefined' && typeof performance.now === 'function')
                        ? performance.now()
                        : Date.now()
                ) - startedAt;
                API.logUiFlow('library_explorer', 'load_more', 'ok', {
                    library_id: String(state.selectedLibraryId || ''),
                    offset: nextOffset,
                    limit: PREVIEW_BATCH_SIZE,
                    returned: Array.isArray(moreData && moreData.images) ? moreData.images.length : 0,
                    total_count: Number((moreData && moreData.count) || totalCount || 0),
                }, durationMs).catch(() => {});
            } catch (err) {
                const durationMs = (
                    (typeof performance !== 'undefined' && typeof performance.now === 'function')
                        ? performance.now()
                        : Date.now()
                ) - startedAt;
                API.logUiFlow('library_explorer', 'load_more', 'error', {
                    library_id: String(state.selectedLibraryId || ''),
                    offset: nextOffset,
                    limit: PREVIEW_BATCH_SIZE,
                    message: String((err && err.message) || err || 'load_failed').slice(0, 160),
                }, durationMs).catch(() => {});
                if (typeof UI.showToast === 'function') {
                    UI.showToast('Preview load failed', 'Could not load more library thumbnails. Scroll again to retry.', 3200);
                }
            } finally {
                isFetching = false;
            }
        }
    };
}
